import { Chip, Stack, Typography } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import { C1IconButton } from 'c1ui'
import { C1Card } from 'components/core/card/C1Card'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { EmptyList } from 'components/core/empty-list'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { SmartTabs } from 'components/core/tabs'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { DisplayAlert, getDisplayAlert } from 'components/pages/explore/inventory/alert-util'
import { BellRing, X } from 'lucide-react'
import { AppResourceRef } from 'pbts/c1/api/app/v1/app_resource'
import { AppUserRef } from 'pbts/c1/api/app/v1/app_user'
import {
    InventoryAlertStatus,
    InventoryAlertExpandMask,
    SearchHistoricalInventoryAlertsRequest,
    SearchInventoryAlertsRequest,
} from 'pbts/c1/api/inventory/v1/inventory_alerts'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { historicalInventoryAlertsSearchApi, HistoricalInventoryAlertValue, inventoryAlertsSearchApi, InventoryAlertValue } from 'redux_toolkit/apps'
import { InventoryAlertStrength } from './InventoryAlertStrength'

type InventoryAlertsListProps = {
    appId: string,
    appUserId?: string,
    appResourceId?: string,
    appResourceTypeId?: string,
    closeDrawer: () => void,
    showSelectedAlert: (expandedAlert: InventoryAlertValue) => void,
}

type InventoryAlertsTabProps = {
    appId: string,
    appUserId?: string,
    appResourceId?: string,
    appResourceTypeId?: string,
    showSelectedAlert: (expandedAlert: InventoryAlertValue | HistoricalInventoryAlertValue) => void,
}

export const InventoryAlertsList = ({
    appId, appUserId, appResourceId, appResourceTypeId, closeDrawer, showSelectedAlert,
}: InventoryAlertsListProps): ReactElement => {
    const intl = useIntl()

    const tabs = [
        {
            key: 'active',
            title: intl.formatMessage({
                defaultMessage: 'Active',
                id: 'inventory_alerts_active_tab',
            }),
            tab: <ActiveInventoryAlertsList
                appId={appId}
                appUserId={appUserId}
                appResourceId={appResourceId}
                appResourceTypeId={appResourceTypeId}
                showSelectedAlert={showSelectedAlert}
            />,
        },
        {
            key: 'historical',
            title: intl.formatMessage({
                defaultMessage: 'Historical',
                id: 'inventory_alerts_historical_tab',
            }),
            tab: <HistoricalInventoryAlertsList
                appId={appId}
                appUserId={appUserId}
                appResourceId={appResourceId}
                appResourceTypeId={appResourceTypeId}
                showSelectedAlert={showSelectedAlert}
            />,
        },
    ]
    return <C1DrawerInner>
        <C1DrawerInner.Header actions={<C1IconButton
            onClick={closeDrawer}
            aria-label={intl.formatMessage({
                id: 'components_inventory_alerts_list_close_drawer',
                defaultMessage: 'Close alerts drawer',
            })}
        >
            <C1Icon icon={X} size="sm" />
        </C1IconButton>}
        >
            <Stack direction="row" alignItems="center" gap={1} sx={{ pt: 2 }}>
                <C1Icon icon={BellRing} sx={{ color: 'warning.main' }} size="md" />
                <Typography variant="h4" component="span" sx={{ ml: 1 }}>
                    <FormattedMessage id="inventory_alerts_drawer_title" defaultMessage="Alerts" />
                </Typography>
            </Stack>
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <SmartTabs tabs={tabs} defaultTab="active" menuKey="inventory_alerts_drawer" />
        </C1DrawerInner.Content>
    </C1DrawerInner>
}

const ActiveInventoryAlertsList = ({
    appId, appUserId, appResourceId, appResourceTypeId, showSelectedAlert,
}: InventoryAlertsTabProps): ReactElement => {
    const intl = useIntl()
    const {
        data,
        isError: isAlertError,
        isLoading: isAlertLoading,
        error: alertFetchError,
    } = inventoryAlertsSearchApi.useSearchInventoryAlerts(SearchInventoryAlertsRequest.create({
        appIds: [appId],
        appUserRefs: appUserId ? [AppUserRef.create({ appId, id: appUserId })] : [],
        identityAppUserRefs: appUserId ? [AppUserRef.create({ appId, id: appUserId })] : [],
        appResourceRefs: appResourceTypeId && appResourceId ?
            [AppResourceRef.create({ appId, id: appResourceId, appResourceTypeId })] : [],
        expandMask: InventoryAlertExpandMask.create({
            paths: ['app_resource', 'app_resource_type', 'app', 'app_user'],
        }),
    }))

    const alerts: readonly InventoryAlertValue[] = data?.[0] || []

    if (isAlertLoading) {
        return <Stack p={4}>
            <Skeleton animation="wave" height="100px" />
            <Skeleton animation="wave" height="100px" />
            <Skeleton animation="wave" height="100px" />
        </Stack>
    }

    if (isAlertError) {
        return <APIError
            title={<FormattedMessage id="components_iventory_alerts_fetch_error" defaultMessage="Error loading alerts related to account" />}
            err={alertFetchError}
        />
    }

    const alertsToDisplay = alerts.map((alert) => getDisplayAlert(intl, alert)).filter((alert) => alert !== null) as DisplayAlert[]

    const handleAlertClick = (alert: DisplayAlert): void => {
        const alertExpanded = alerts.find((a) => a.inventoryAlert?.id === alert?.id)
        if (alertExpanded) {
            showSelectedAlert(alertExpanded)
        }
    }

    return (alertsToDisplay.length === 0
        ? <EmptyList
            icon={<C1Icon icon={BellRing} sx={{ color: 'text.disabled' }} />}
            text={<FormattedMessage defaultMessage="No alerts found" id="inventory_alerts_empty_state" />}
        />
        : <>
            {alertsToDisplay
                .filter((alert) => alert !== null)
                .map((alert) => (alert &&
                    <C1Card
                        key={alert.id}
                        variant="outlined"
                        onClick={(): void => handleAlertClick(alert)}
                    >
                        <Stack spacing={2}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography
                                    variant="h6"
                                    component="span"
                                    sx={sxc([alert.status === InventoryAlertStatus.RESOLVED, { color: 'text.disabled' }])}
                                >
                                    {alert.title}
                                </Typography>
                                {alert.status === InventoryAlertStatus.RESOLVED && (
                                    <Chip
                                        color="info"
                                        label={intl.formatMessage({
                                            id: 'app_details_access_controls_app_requests_on',
                                            defaultMessage: 'Resolved',
                                        })}
                                    />
                                )}
                                <InventoryAlertStrength severity={alert.severity} />
                            </Stack>
                            <Typography color="text.secondary" sx={sxc([alert.status === InventoryAlertStatus.RESOLVED, { color: 'text.disabled' }])}>
                                {alert.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={sxc([alert.status === InventoryAlertStatus.RESOLVED, { color: 'text.disabled' }])}>
                                {alert.detectedOn}
                            </Typography>
                        </Stack>
                    </C1Card>
                ))}
        </>
    )
}

const HistoricalInventoryAlertsList = ({
    appId, appUserId, appResourceId, appResourceTypeId, showSelectedAlert,
}: InventoryAlertsTabProps): ReactElement => {
    const intl = useIntl()
    const {
        data,
        isError: isAlertError,
        isLoading: isAlertLoading,
        error: alertFetchError,
    } = historicalInventoryAlertsSearchApi.useSearchHistoricalInventoryAlerts(SearchHistoricalInventoryAlertsRequest.create({
        appIds: [appId],
        appUserRefs: appUserId ? [AppUserRef.create({ appId, id: appUserId })] : [],
        identityAppUserRefs: appUserId ? [AppUserRef.create({ appId, id: appUserId })] : [],
        appResourceRefs: appResourceTypeId && appResourceId ?
            [AppResourceRef.create({ appId, id: appResourceId, appResourceTypeId })] : [],
        expandMask: InventoryAlertExpandMask.create({
            paths: ['app_resource', 'app_resource_type', 'app', 'app_user'],
        }),
    }))

    const alerts: readonly HistoricalInventoryAlertValue[] = data?.[0] || []

    if (isAlertLoading) {
        return <Stack p={4}>
            <Skeleton animation="wave" height="100px" />
            <Skeleton animation="wave" height="100px" />
            <Skeleton animation="wave" height="100px" />
        </Stack>
    }

    if (isAlertError) {
        return <APIError
            title={<FormattedMessage id="components_iventory_alerts_fetch_error" defaultMessage="Error loading alerts related to account" />}
            err={alertFetchError}
        />
    }

    const alertsToDisplay = alerts.map((alert) => getDisplayAlert(intl, alert)).filter((alert) => alert !== null) as DisplayAlert[]

    const handleAlertClick = (alert: DisplayAlert): void => {
        const alertExpanded = alerts.find((a) => a.inventoryAlert?.id === alert?.id)
        if (alertExpanded) {
            showSelectedAlert(alertExpanded)
        }
    }

    return (alertsToDisplay.length === 0
        ? <EmptyList
            icon={<C1Icon icon={BellRing} sx={{ color: 'text.disabled' }} />}
            text={<FormattedMessage defaultMessage="No alerts found" id="inventory_alerts_empty_state" />}
        />
        : <>
            {alertsToDisplay
                .filter((alert) => alert !== null)
                .map((alert) => (alert &&
                    <C1Card
                        key={alert.id}
                        variant="outlined"
                        onClick={(): void => handleAlertClick(alert)}
                    >
                        <Stack spacing={2}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography
                                    variant="h6"
                                    sx={sxc([alert.status === InventoryAlertStatus.RESOLVED, { color: 'text.disabled' }])}
                                >
                                    {alert.title}
                                </Typography>
                                {alert.status === InventoryAlertStatus.RESOLVED && (
                                    <Chip
                                        color="info"
                                        label={intl.formatMessage({
                                            id: 'app_details_access_controls_app_requests_on',
                                            defaultMessage: 'Resolved',
                                        })}
                                    />
                                )}
                                <InventoryAlertStrength severity={alert.severity} />
                            </Stack>
                            <Typography color="text.secondary" sx={sxc([alert.status === InventoryAlertStatus.RESOLVED, { color: 'text.disabled' }])}>
                                {alert.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={sxc([alert.status === InventoryAlertStatus.RESOLVED, { color: 'text.disabled' }])}>
                                {alert.detectedOn}
                            </Typography>
                        </Stack>
                    </C1Card>
                ))}
        </>
    )
}
